import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'reactstrap';
import { formatUnixToDate, formatNumberEng } from '_utils/formatNumber';
import MemberAvatar from '../MemberAvatar';
import useMemberPurchases from '../../hooks/useMemberPurchases';
import { ROLE_TYPES } from '../../constant';
import './style.scss';

const ModalMemberDetail = ({
  isOpen,
  toggle,
  member = null,
  groupId = null,
  isOwner = false,
  canModify = true,
  onBan = null,
  onRemove = null,
  onChangeRole = null,
  onWhatsApp = null,
}) => {
  const { purchases, loading: loadingPurchases } = useMemberPurchases({
    userId: member?.uid,
    groupId,
    enabled: isOpen,
  });
  const currentRole = (member?.role || ROLE_TYPES.MEMBER).toLowerCase();

  const displayName = member?.uName || member?.name || 'Member';

  const [textBtnBan, setTextBtnBan] = useState(
    member?.banned === 1 || member?.isBanned ? 'Banned' : 'Ban'
  );

  useEffect(() => {
    setTextBtnBan(member?.banned === 1 || member?.isBanned ? 'Banned' : 'Ban');
  }, [member?.banned, member?.isBanned]);

  const handleRoleChange = useCallback(
    async (e) => {
      if (!canModify) return;
      const newRole = e.target.value;
      if (newRole === currentRole) return;

      if (onChangeRole) {
        await onChangeRole(member, newRole);
      }
    },
    [canModify, currentRole, onChangeRole, member]
  );

  const handleWhatsAppClick = useCallback(() => {
    if (onWhatsApp) onWhatsApp(member);
  }, [onWhatsApp, member]);

  const handleBanClick = useCallback(async () => {
    if (!canModify) return;
    const prevText = textBtnBan;
    const nextText = prevText === 'Ban' ? 'Banned' : 'Ban';
    setTextBtnBan(nextText);
    try {
      if (onBan) {
        await onBan(member);
      }
    } catch (error) {
      setTextBtnBan(prevText);
    }
  }, [canModify, onBan, member, textBtnBan]);

  const handleRemoveClick = useCallback(async () => {
    if (!canModify) return;
    if (onRemove) {
      await onRemove(member, () => {
        toggle?.();
      });
    }
  }, [canModify, onRemove, member, toggle]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="modal-member-detail-wrapper"
    >
      <div className="modal-hd">
        <span className="modal-title">{displayName}</span>
        <button type="button" className="modal-close" onClick={toggle}>
          ×
        </button>
      </div>

      <div className="member-hero">
        <MemberAvatar
          member={member}
          name={displayName}
          className="member-hero-avatar"
        >
          <div
            className="whatsapp-badge"
            title="Contact on WhatsApp"
            onClick={handleWhatsAppClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleWhatsAppClick()}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.122 1.523 5.855L.057 23.082a.75.75 0 0 0 .916.916l5.228-1.466A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.93 0-3.736-.518-5.29-1.424l-.378-.224-3.905 1.095 1.094-3.906-.224-.378A9.953 9.953 0 0 1 2 12C2 6.478 6.478 2 12 2s10 4.478 10 10-4.478 10-10 10z" />
            </svg>
          </div>
        </MemberAvatar>

        <div className="member-hero-name">{displayName}</div>
        <div className="member-hero-contact">
          {[member?.phone, member?.email].filter(Boolean).join(' · ')}
        </div>

        <div className="member-hero-actions">
          {canModify && (
            <>
              <button
                type="button"
                className={textBtnBan === 'Banned' ? 'btn-outline' : 'btn-danger-outline'}
                onClick={handleBanClick}
              >
                {textBtnBan}
              </button>
              <button
                type="button"
                className="btn-danger-outline"
                onClick={handleRemoveClick}
              >
                Remove
              </button>
            </>
          )}
          <button
            type="button"
            className="btn-outline"
            onClick={handleWhatsAppClick}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Message
          </button>
        </div>
      </div>

      <div className="modal-section">
        <div className="modal-section-title">Member Details</div>
        <div className="details-view-list">
          <div className="detail-item">
            <span className="item-label">Name</span>
            <span className="item-value">{member?.uName || member?.name || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="item-label">Phone</span>
            <span className="item-value teal">{member?.phone || '-'}</span>
          </div>
          <div className="detail-item">
            <span className="item-label">Email</span>
            <span className="item-value teal">{member?.email || '-'}</span>
          </div>
        </div>
      </div>

      <div className="modal-section">
        <div className="modal-section-title">User Rights {isOwner ? '(Owner)' : ''}</div>
        <div className="rights-row">
          {['admin', 'host', 'member'].map((r) => (
            <label key={r} className="right-option" style={{ cursor: !canModify ? 'not-allowed' : 'pointer' }}>
              <input
                type="radio"
                name="role"
                value={r}
                checked={currentRole === r}
                onChange={handleRoleChange}
                disabled={!canModify}
                style={{ cursor: !canModify ? 'not-allowed' : 'pointer' }}
              />
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </label>
          ))}
        </div>
      </div>

      <div className="modal-section">
        <div className="modal-section-title">User Verification Response</div>
        <div className="verification-response">
          {member?.answer ? (
            <div className="response-text">{member.answer}</div>
          ) : (
            <div className="empty-response">No verification response submitted</div>
          )}
        </div>
      </div>

      <div className="modal-section">
        <div className="modal-section-title dark">
          Past Purchases
        </div>
        <div className="purchases-list">
          {loadingPurchases ? (
            <div className="empty-purchases">Loading purchases...</div>
          ) : purchases.length > 0 ? (
            purchases.map((item, idx) => (
              <div key={idx} className="purchase-row">
                <span className="purchase-event">{item?.eTitle || 'Unknown Event'}</span>
                <span className="purchase-date">{formatUnixToDate(item?.ePickupTimestamp)}</span>
                <span className="purchase-price"> ${formatNumberEng(item?.totalPrice || 0, 1)}</span>
              </div>
            ))
          ) : (
            <div className="empty-purchases">No past purchases</div>
          )}
        </div>
      </div>

      <div className="modal-footer">
        <button type="button" className="modal-btn-cancel" onClick={toggle}>
          Close
        </button>
      </div>
    </Modal>
  );
};

ModalMemberDetail.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  member: PropTypes.shape({
    uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    uName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    photoUrl: PropTypes.string,
    answer: PropTypes.string,
    role: PropTypes.string,
    banned: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    isBanned: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  }),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOwner: PropTypes.bool,
  canModify: PropTypes.bool,
  onBan: PropTypes.func,
  onRemove: PropTypes.func,
  onChangeRole: PropTypes.func,
  onWhatsApp: PropTypes.func,
};

export default memo(ModalMemberDetail);
